import React, { useState, useEffect, useContext } from 'react';
import { createIssue, fetchUsers } from '../../services/apiService';
import Select from 'react-select';
import { UserContext } from '../userContext/UserContext';
import './create.css';

const Create = () => {
  const today = new Date().toISOString().slice(0, 10);

  const [issue, setIssue] = useState({
    Status: '',
    Title: '',
    IssueName: '',
    Severity: '',
    ReportedByName: '',
    ReportedById: '',
    DateReported: today,
    AssignedToName: '',
    AssignedToId: '',
    DueDate: '',
    Team: '',
    Description: '',
    LessonsLearned: false,
    IntExt: ['Internal'] // Default value: Internal is selected
  });

  const [userMap, setUserMap] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [attachmentPreviews, setAttachmentPreviews] = useState([]);
  const { role, userId, ensureValidSasToken } = useContext(UserContext);

  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function getUsers() {
      const users = await fetchUsers();
      const map = {};
      users.forEach(user => {
        map[user.id] = user.displayName;
      });
      setUserMap(map);
      // If a userId exists, update the issue with the current user's id and name.
      if (userId) {
        setIssue(prevIssue => ({
          ...prevIssue,
          ReportedById: userId,
          ReportedByName: map[userId] || ''
        }));
      }
    }
    getUsers();
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setIssue({
      ...issue,
      [name]: value,
    });
  };

  const handleAttachmentChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments([...attachments, ...files]);
    setAttachmentPreviews([...attachmentPreviews, ...files]);
  };

  const handleAttachmentRemove = (index) => {
    const newAttachments = [...attachments];
    const newPreviews = [...attachmentPreviews];
    newAttachments.splice(index, 1);
    newPreviews.splice(index, 1);
    setAttachments(newAttachments);
    setAttachmentPreviews(newPreviews);
  };

  // Modified handler for Internal/External checkboxes
  const handleIntExtCheckbox = (value, isChecked) => {
    // If unchecking the only remaining option, do nothing.
    if (!isChecked && issue.IntExt.length === 1) {
      return;
    }
    if (isChecked) {
      setIssue(prevIssue => ({
        ...prevIssue,
        IntExt: prevIssue.IntExt.includes(value)
          ? prevIssue.IntExt
          : [...prevIssue.IntExt, value]
      }));
    } else {
      setIssue(prevIssue => ({
        ...prevIssue,
        IntExt: prevIssue.IntExt.filter(item => item !== value)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return; // Prevent multiple clicks

    setLoading(true); // Disable button while processing

    const issueData = {
      Status: issue.Status,
      Title: issue.Title,
      IssueName: issue.IssueName,
      Severity: issue.Severity,
      ReportedByName: userMap[issue.ReportedById],
      ReportedById: issue.ReportedById,
      DateReported: issue.DateReported,
      AssignedToName: issue.AssignedToName,
      AssignedToId: issue.AssignedToId,
      DueDate: issue.DueDate || null,
      Team: issue.Team,
      Description: issue.Description,
      LessonsLearned: issue.LessonsLearned,
      IntExt: issue.IntExt // Use the dynamic array value
    };

    try {
      // Ensure SAS token is valid before uploading attachments
      if (attachments.length > 0) {
        await ensureValidSasToken();
      }
      
      await createIssue(issueData, attachments); // API Call
      setSuccessMessage('Issue Created Successfully');
      setErrorMessage('');

      // Reset form fields after successful submission
      setIssue({
        Status: '',
        Title: '',
        IssueName: '',
        Severity: '',
        ReportedByName: '',
        ReportedById: '',
        DateReported: today,
        AssignedToName: '',
        AssignedToId: '',
        DueDate: '',
        Team: '',
        Description: '',
        LessonsLearned: false,
        IntExt: ['Internal'] // Reset to default: Internal selected
      });
      setAttachments([]);
      setAttachmentPreviews([]);
    } catch (error) {
      setErrorMessage('Failed to create issue: ' + error.message);
      setSuccessMessage('');
    } finally {
      setLoading(false); // Re-enable button after completion
    }
  };

  return (
    <div className="modal-container">
      <h1>Create New Issue</h1>
      <form className="modal-form" onSubmit={handleSubmit}>
        {/* Display success or error messages */}
        {successMessage && <p className="success-message">{successMessage}</p>}
        {errorMessage && <p className="error-message">{errorMessage}</p>}

        {/* Combined Internal/External and Lessons Learned Row */}
        <div className="modal-form-row internal-lessons-row">
          <div className="modal-form-group internal-external-group">
            <label className="group-label">Internal/External:</label>
            <div className="checkbox-group">
              <label>
              <span>Internal</span>
                <input
                  type="checkbox"
                  name="internal"
                  checked={issue.IntExt.includes('Internal')}
                  onChange={(e) => {
                    if (!e.target.checked && issue.IntExt.length === 1) return;
                    handleIntExtCheckbox('Internal', e.target.checked);
                  }}
                />
                
              </label>
              <label>
              <span>External</span>
                <input
                  type="checkbox"
                  name="external"
                  checked={issue.IntExt.includes('External')}
                  onChange={(e) => {
                    if (!e.target.checked && issue.IntExt.length === 1) return;
                    handleIntExtCheckbox('External', e.target.checked);
                  }}
                />
                
              </label>
            </div>
          </div>
          <div className="modal-form-group lessons-learned-group">
            <label>
              Lessons Learned
              <input
                type="checkbox"
                name="LessonsLearned"
                checked={issue.LessonsLearned}
                onChange={(e) =>
                  setIssue({ ...issue, LessonsLearned: e.target.checked })
                }
              />
            </label>
          </div>
        </div>

        {/* Group Job Number, Issue Name, Severity, and Date Reported */}
        <div className="modal-form-row">
          <div className="modal-form-group jobnum-group">
            <label>Job #*:</label>
            <input
              type="text"
              name="Title"
              value={issue.Title}
              onChange={handleChange}
              required
            />
          </div>
          <div className="modal-form-group create-name">
            <label>Issue Name*:</label>
            <input
              type="text"
              name="IssueName"
              value={issue.IssueName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="modal-form-group create-severity">
            <label>Severity*:</label>
            <select
              name="Severity"
              value={issue.Severity}
              onChange={handleChange}
              required
            >
              <option value="">Select Severity</option>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
          </div>
          <div className="modal-form-group date-group">
            <label>Date Reported:</label>
            <input
              type="date"
              name="DateReported"
              value={issue.DateReported}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        {/* Group Reported By, Assigned To, Team, and Due Date */}
        <div className="modal-form-row">
          <div className="modal-form-group report-group">
            <label>Reported By:</label>
            <select
              name="ReportedById"
              value={issue.ReportedById}
              onChange={handleChange}
              disabled={role !== 'Role.Admin'}
            >
              <option value="">Select User</option>
              {Object.keys(userMap).map(userId => (
                <option key={userId} value={userId}>
                  {userMap[userId]}
                </option>
              ))}
            </select>
          </div>
          <div className="modal-form-group assign-group">
            <label>Assigned To:</label>
            <Select
              isMulti
              name="AssignedToId"
              value={
                issue.AssignedToId
                  ? issue.AssignedToId.split(',').map(userId => ({ value: userId, label: userMap[userId] }))
                  : []
              }
              onChange={(selectedOptions) => {
                const selectedUserIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
                const selectedUserNames = selectedOptions ? selectedOptions.map(option => userMap[option.value]) : [];

                const assignedToIds = selectedUserIds.length ? selectedUserIds.join(',') : '';
                const assignedToNames = selectedUserNames.length ? selectedUserNames.join(', ') : '';

                setIssue({
                  ...issue,
                  AssignedToId: assignedToIds,
                  AssignedToName: assignedToNames,
                });
              }}
              options={Object.keys(userMap).map(userId => ({
                value: userId,
                label: userMap[userId],
              }))}
              isDisabled={role === 'Role.User'}
              maxMenuHeight={250}
              menuShouldScrollIntoView={false}
            />
          </div>
          <div className="modal-form-group team-group">
            <label>Team:</label>
            <select
              name="Team"
              value={issue.Team}
              onChange={handleChange}
              disabled={role !== 'Role.Admin'}
            >
              <option value="">Select Team</option>
              <option value="Mechanical Design">Mechanical Design</option>
              <option value="Controls Design">Controls Design</option>
              <option value="Mechanical Build">Mechanical Build</option>
              <option value="Electrical Build">Electrical Build</option>
              <option value="Project Management">Project Management</option>
            </select>
          </div>
          <div className="modal-form-group date-group">
            <label>Due Date:</label>
            <input
              type="date"
              name="DueDate"
              value={issue.DueDate}
              onChange={handleChange}
              disabled={role !== 'Role.Admin'}
            />
          </div>
        </div>

        {/* Full-width Description */}
        <div className="modal-form-group">
          <label>Description:</label>
          <textarea
            name="Description"
            value={issue.Description}
            onChange={handleChange}
          />
        </div>

        {/* Attachment Upload Section */}
        <div className="modal-form-group">
          <label>Attachments:</label>
          <input
            type="file"
            multiple
            onChange={handleAttachmentChange}
          />
          {attachmentPreviews.length > 0 && (
            <div className="attachment-preview-container">
              <div className="attachments-container">
                {attachmentPreviews.map((file, index) => {
                  const fileType = file.type || '';
                  const fileUrl = URL.createObjectURL(file);

                  if (fileType.startsWith('image/')) {
                    return (
                      <div key={index} className="attachment-preview">
                        <img
                          src={fileUrl}
                          alt="Preview"
                          className="attachment-image"
                        />
                        <button
                          type="button"
                          className="remove-attachment-button"
                          onClick={() => handleAttachmentRemove(index)}
                        >
                          &times;
                        </button>
                      </div>
                    );
                  } else if (fileType.startsWith('video/')) {
                    return (
                      <div key={index} className="attachment-preview video-preview">
                        <div className="video-play-icon">&#9658;</div>
                        <button
                          type="button"
                          className="remove-attachment-button"
                          onClick={() => handleAttachmentRemove(index)}
                        >
                          &times;
                        </button>
                      </div>
                    );
                  } else {
                    return (
                      <div key={index} className="attachment-preview file-preview">
                        <div className="file-icon">&#128196;</div>
                        <button
                          type="button"
                          className="remove-attachment-button"
                          onClick={() => handleAttachmentRemove(index)}
                        >
                          &times;
                        </button>
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          )}
        </div>

        <button type="submit" className="submit-button" disabled={loading}>
          {loading ? 'Creating...' : 'Create Issue'}
        </button>
      </form>
    </div>
  );
};

export default Create;
